// Централізована логіка формування ціни — ЄДИНЕ джерело правди для PDP,
// каталогу, кошика, чекауту та серверного precompute.
//
// Ланцюг націнок (усі налаштовуються в адмінці):
//   базова ціна регіону
//     → + націнка типу видачі (Ключ/Гіфт/Акаунт/Оренда)   offerMarkupPct
//     → + націнка регіону                                  regionMarkupPct
//     → конвертація в USD (базова валюта розрахунків)
//     → + націнка курсу відображуваної валюти              rate.markup  (RUB/UAH)
//
// Чисті функції без залежностей — покриваються тестами (pricing.test.ts).

export interface CurrencyRate {
  value: number;   // 1 USD = value одиниць цієї валюти
  markup: number;  // додаткова націнка курсу, %
  label: string;   // ₽ / ₴ / $
  locale: string;  // ru-RU / uk-UA / en-US
}
export type DisplayRates = Record<string, CurrencyRate>;

/**
 * Базова ціна регіону → USD із урахуванням націнки типу видачі та регіону.
 * @param preUsd  якщо ціну вже пораховано на сервері — повертається напряму (націнки НЕ додаються повторно).
 * @param fxRegionToUsd  1 одиниця валюти регіону = скільки USD? Ні — це `units per USD` (напр. UAH: 41).
 *        USD отримуємо діленням: base / fxRegionToUsd. Для валюти USD параметр ігнорується.
 */
export function offerUsd(params: {
  listPrice: number;
  currencyCode: string;
  offerMarkupPct?: number;
  regionMarkupPct?: number;
  fxRegionToUsd?: number | null;
  preUsd?: number | null;
}): number | null {
  const { listPrice, currencyCode, offerMarkupPct = 0, regionMarkupPct = 0, fxRegionToUsd, preUsd } = params;
  if (preUsd != null) return preUsd;
  const base = listPrice * (1 + offerMarkupPct / 100) * (1 + regionMarkupPct / 100);
  if (currencyCode === 'USD') return base;
  if (!fxRegionToUsd) return null;
  return base / fxRegionToUsd;
}

/**
 * USD → відображувана валюта з націнкою курсу.
 * Повертає число в цільовій валюті (не форматоване). Для USD (rate відсутній) — повертає usd як є.
 */
export function displayFromUsd(usd: number | null, rate: CurrencyRate | undefined | null): number | null {
  if (usd == null) return null;
  if (!rate || rate.value === 1) return usd;
  return usd * rate.value * (1 + rate.markup / 100);
}

/**
 * Повний ланцюг: регіон → USD → відображувана валюта. Округлення — на боці відображення.
 */
export function finalDisplayPrice(params: {
  listPrice: number;
  currencyCode: string;
  offerMarkupPct?: number;
  regionMarkupPct?: number;
  fxRegionToUsd?: number | null;
  preUsd?: number | null;
  rate: CurrencyRate | undefined | null;
}): number | null {
  const usd = offerUsd(params);
  return displayFromUsd(usd, params.rate);
}

export const OFFER_TYPES = ['key', 'gift', 'account', 'rental'] as const;
export type OfferKey = typeof OFFER_TYPES[number];

/**
 * Серверне: повертає повний набір фінальних USD-цін по типах видачі.
 * Наявні (порахований воркером prices_usd) — беруться як є; відсутні — рахуються
 * з базової ціни регіону та націнки типу видачі. Клієнту віддаємо ЛИШЕ результат —
 * без базової ціни й відсотків націнок.
 */
export function completeOfferUsd(params: {
  listPrice: number;
  currencyCode: string;
  fxRegionToUsd?: number | null;
  markups: Partial<Record<string, number>>;
  existing?: Partial<Record<string, number>> | null;
}): Record<string, number> {
  const out: Record<string, number> = {};
  for (const o of OFFER_TYPES) {
    const pre = params.existing?.[o];
    if (pre != null) { out[o] = pre; continue; }
    const v = offerUsd({
      listPrice: params.listPrice,
      currencyCode: params.currencyCode,
      offerMarkupPct: params.markups[o] ?? 0,
      fxRegionToUsd: params.fxRegionToUsd,
    });
    if (v != null) out[o] = v;
  }
  return out;
}

/**
 * Ціна «до знижки» (перекреслена) у USD — виводиться з фінальної ціни та % знижки,
 * БЕЗ розкриття базової ціни/маржі. finalUsd / (1 - знижка%).
 */
export function msrpUsdFromDiscount(finalUsd: number | null, discountPercent: number | null | undefined): number | null {
  if (finalUsd == null || !discountPercent || discountPercent <= 0 || discountPercent >= 100) return null;
  return finalUsd / (1 - discountPercent / 100);
}

/** Форматування підсумкової суми у відображуваній валюті (число вже сконвертоване). */
export function formatDisplay(amount: number | null, rate: CurrencyRate | undefined | null): string {
  if (amount == null) return '—';
  if (!rate || rate.value === 1) {
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount); }
    catch { return `$${amount.toFixed(2)}`; }
  }
  return `${Math.round(amount).toLocaleString(rate.locale)} ${rate.label}`;
}
